import React from 'react';
import { Clock, Database, DollarSign, CheckCircle, AlertCircle, Loader } from 'lucide-react';

const AnalysisStatusModal = ({ isVisible, status, onClose }) => {
  if (!isVisible) return null;

  const formatTime = (milliseconds) => {
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    if (minutes > 0) {
      return `${minutes}分${remainingSeconds}秒`;
    }
    return `${remainingSeconds}秒`;
  };

  const formatTokens = (tokens) => {
    if (tokens >= 1000) {
      return `${(tokens / 1000).toFixed(1)}K`;
    }
    return tokens.toString();
  };

  const formatCost = (cost) => {
    return `¥${cost.toFixed(6)}`;
  };

  const getPhaseIcon = (phase) => {
    switch (phase) {
      case 'preparing':
        return <Loader className="w-5 h-5 text-blue-500 animate-spin" />;
      case 'creating_prompt':
        return <Loader className="w-5 h-5 text-blue-500 animate-spin" />;
      case 'calling_api':
        return <Loader className="w-5 h-5 text-yellow-500 animate-spin" />;
      case 'processing_response':
        return <Loader className="w-5 h-5 text-green-500 animate-spin" />;
      case 'finalizing':
        return <Loader className="w-5 h-5 text-purple-500 animate-spin" />;
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Loader className="w-5 h-5 text-blue-500 animate-spin" />;
    }
  };

  const getPhaseColor = (phase) => {
    switch (phase) {
      case 'preparing':
      case 'creating_prompt':
        return 'text-blue-600';
      case 'calling_api':
        return 'text-yellow-600';
      case 'processing_response':
        return 'text-green-600';
      case 'finalizing':
        return 'text-purple-600';
      case 'completed':
        return 'text-green-600';
      case 'error':
        return 'text-red-600';
      default:
        return 'text-blue-600';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">分析狀態</h3>
          {status.phase === 'completed' && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Status Message */}
        <div className="flex items-center space-x-3 mb-6">
          {getPhaseIcon(status.phase)}
          <span className={`font-medium ${getPhaseColor(status.phase)}`}>
            {status.message}
          </span>
        </div>

        {/* Progress Indicators */}
        <div className="space-y-4">
          {/* Time Elapsed */}
          <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
            <div className="flex items-center space-x-2">
              <Clock className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-900">耗時</span>
            </div>
            <span className="text-sm font-semibold text-blue-900">
              {formatTime(status.timeElapsed)}
            </span>
          </div>

          {/* Tokens Used */}
          <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
            <div className="flex items-center space-x-2">
              <Database className="w-4 h-4 text-green-600" />
              <span className="text-sm font-medium text-green-900">Token 使用量</span>
            </div>
            <div className="text-right">
              <div className="text-sm font-semibold text-green-900">
                輸入: {formatTokens(status.tokensUsed.input)}
              </div>
              <div className="text-sm font-semibold text-green-900">
                輸出: {formatTokens(status.tokensUsed.output)}
              </div>
            </div>
          </div>

          {/* Cost */}
          <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
            <div className="flex items-center space-x-2">
              <DollarSign className="w-4 h-4 text-yellow-600" />
              <span className="text-sm font-medium text-yellow-900">費用</span>
            </div>
            <div className="text-right">
              <div className="text-sm font-semibold text-yellow-900">
                輸入: {formatCost(status.cost.inputCost)}
              </div>
              <div className="text-sm font-semibold text-yellow-900">
                輸出: {formatCost(status.cost.outputCost)}
              </div>
              <div className="text-sm font-bold text-yellow-900 border-t border-yellow-200 pt-1 mt-1">
                總計: {formatCost(status.cost.totalCost)}
              </div>
            </div>
          </div>

          {/* Discount Time Indicator */}
          {status.cost.isDiscountTime && (
            <div className="flex items-center justify-center p-2 bg-purple-50 rounded-lg">
              <span className="text-xs font-medium text-purple-700">
                🕐 優惠時段 (00:30-08:30) - 享受折扣價格
              </span>
            </div>
          )}

          {/* Model Information */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <span className="text-sm font-medium text-gray-700">模型</span>
            <span className="text-sm font-semibold text-gray-900">
              {status.cost.model === 'deepseek-reasoner' ? 'DeepSeek Reasoner' : 'DeepSeek Chat'}
            </span>
          </div>

          {/* Fallback Data Indicator */}
          {status.cost.fallbackUsed && (
            <div className="flex items-center justify-center p-2 bg-orange-50 rounded-lg">
              <span className="text-xs font-medium text-orange-700">
                ⚠️ 使用備用數據 - API解析失敗，已生成基本知識圖譜
              </span>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        {status.phase === 'completed' && (
          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              關閉
            </button>
          </div>
        )}

        {status.phase === 'error' && (
          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              關閉
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalysisStatusModal; 